<script setup lang="ts">
import CardSpadeIcon from '~/components/icons/CardSpadeIcon.vue'
import PhonesNumberIcon from '~/components/icons/PhonesNumberIcon.vue'
import LampLightIcon from '~/components/icons/LampLightIcon.vue'
import { combinations } from '~/utils/constants/combinations'
import { toThreeDecimals } from '~/utils/numbers'

defineOptions({
	name: 'MultipleBetsCard',
})

interface Props {
	modelValue?: boolean
	numbers?: any
	rules?: any
}
const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
	numbers: () => {},
	rules: () => {},
})

const { t: $t } = useI18n()
const emit = defineEmits(['update:modelValue', 'updateStatusMutipleBets'])

const isEnabled = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})

const getCombinations = computed(() => {
	if (!props.numbers || !props.rules) return 0
	const { first, last } = props.numbers
	const { first_block, last_block } = props.rules

	const min = first_block.min_select ?? 0
	const counter = first.filter((n: number | null) => n !== null).length
	const firstCombination = combinations(counter, min)
	if (last_block === null) {
		return toThreeDecimals(firstCombination)
	}

	const minLast = last_block.min_select ?? 0
	const counterLast = last.filter((n: number | null) => n !== null).length
	return toThreeDecimals(firstCombination * combinations(counterLast, minLast))
})

const getCombinationsValue = async () => {
	return getCombinations.value
}

defineExpose({
	getCombinationsValue,
})
</script>

<template>
	<div
		class="rounded-lg shadow-lg border border-[#4c3d9c] bg-[linear-gradient(90deg,var(--secondary-600,#2E2948)_0.11%,#4C3D9C_99.71%)]"
	>
		<div class="px-5 sm:px-6 py-5 space-y-11">
			<div class="flex items-start gap-8 sm:gap-10 justify-between w-full">
				<div class="flex flex-col gap-4">
					<div class="flex items-center gap-2">
						<CardSpadeIcon class="text-white size-6" />

						<h3 class="text-base font-bold text-white">{{ $t('Activate Multiple Checks') }}</h3>
					</div>
					<p class="text-xs sm:text-sm text-white">
						{{ $t('Select more numbers to generate multiple combinations and increase your winning chances') }}
					</p>
				</div>
				<UToggle
					v-model="isEnabled"
					@change="emit('updateStatusMutipleBets')"
				/>
			</div>
			<div
				v-if="isEnabled"
				class="flex flex-col sm:flex-row gap-6"
			>
				<div
					class="px-5 py-4 sm:w-72 shrink-0 bg-green-500 border border-green-500 rounded-2xl flex items-center gap-3"
				>
					<div class="bg-[#F7F7F7] rounded-xl py-3 px-2.5">
						<PhonesNumberIcon class="text-[#323232] size-6" />
					</div>
					<div class="text-[#F7F7F7]">
						<h3 class="font-bold text-lg">{{ getCombinations }}</h3>
						<p class="text-sm">{{ $t('possibilities of combinations', getCombinations) }}</p>
					</div>
				</div>
				<div
					class="px-5 py-4 w-full rounded-2xl border border-green-500 bg-[#4C3D9C] flex gap-4 items-center text-white"
				>
					<LampLightIcon class="size-6 shrink-0" />
					<p class="text-xs">
						{{
							$t(
								'By choosing {combination} {text}, you have increased your coverage and your chance of hitting a winning tier by approximately 50% compared to a single line',
								{ combination: getCombinations, text: $t('combinations', getCombinations) },
							)
						}}.
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
